import React from "react";
import { Link } from "react-router-dom";
import {
  FiUsers,
  FiFolder,
  FiFileText,
  FiMessageSquare,
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiArrowRight,
} from "react-icons/fi";
import { useGetAnalyticsQuery } from "../../redux/slices/api/adminApiSlice";
import { Bars } from "react-loader-spinner";

const StatCard = ({ title, value, icon: Icon, color, change, changeType }) => (
  <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-500 text-sm font-medium">{title}</p>
        <p className="text-3xl font-bold text-secondary mt-1">{value}</p>
        {change !== undefined && (
          <div
            className={`flex items-center gap-1 mt-2 text-sm ${
              changeType === "up" ? "text-green-600" : "text-red-600"
            }`}
          >
            {changeType === "up" ? (
              <FiTrendingUp size={14} />
            ) : (
              <FiTrendingDown size={14} />
            )}
            <span>{change}% this month</span>
          </div>
        )}
      </div>
      <div
        className={`w-14 h-14 rounded-xl flex items-center justify-center ${color}`}
      >
        <Icon size={24} className="text-white" />
      </div>
    </div>
  </div>
);

const RecentItem = ({ item, type }) => {
  if (type === "user") {
    const displayName =
      item.role === "organization"
        ? item.fundername
        : `${item.firstname || ""} ${item.lastname || ""}`.trim() || "N/A";

    return (
      <div className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
          {item.images?.profile ? (
            <img
              src={item.images.profile}
              alt={displayName}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-primary font-semibold">
              {displayName[0]?.toUpperCase() || "?"}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-secondary truncate">{displayName}</p>
          <p className="text-sm text-gray-500">{item.email || "No email"}</p>
        </div>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            item.role === "organization"
              ? "bg-purple-100 text-purple-700"
              : "bg-blue-100 text-blue-700"
          }`}
        >
          {item.role}
        </span>
      </div>
    );
  }

  if (type === "project") {
    return (
      <div className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
        <div className="w-10 h-10 rounded-lg bg-primary-green/10 flex items-center justify-center">
          <FiFolder className="text-primary-green" size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-secondary truncate">{item.title}</p>
          <p className="text-sm text-gray-500">{item.category}</p>
        </div>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            item.state === "Open Calls"
              ? "bg-green-100 text-green-700"
              : item.state === "Draft"
              ? "bg-gray-100 text-gray-700"
              : "bg-yellow-100 text-yellow-700"
          }`}
        >
          {item.state}
        </span>
      </div>
    );
  }

  return null;
};

const AdminDashboard = () => {
  const { data, isLoading, error } = useGetAnalyticsQuery();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Bars height="60" width="60" color="#8538FF" ariaLabel="loading" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600">Failed to load analytics. Please try again.</p>
      </div>
    );
  }

  const { overview, growth, userBreakdown, projectBreakdown, recent } = data || {};

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Dashboard</h1>
          <p className="text-gray-500">Welcome back! Here's what's happening.</p>
        </div>
        <div className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleString()}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <StatCard
          title="Total Users"
          value={overview?.totalUsers || 0}
          icon={FiUsers}
          color="bg-primary"
          change={growth?.usersGrowthPercent}
          changeType="up"
        />
        <StatCard
          title="Total Projects"
          value={overview?.totalProjects || 0}
          icon={FiFolder}
          color="bg-primary-green"
        />
        <StatCard
          title="Results"
          value={overview?.totalResults || 0}
          icon={FiFileText}
          color="bg-yellow-500"
        />
        <StatCard
          title="Communities"
          value={overview?.totalCommunities || 0}
          icon={FiMessageSquare}
          color="bg-pink-500"
        />
        <StatCard
          title="Total Funds"
          value={overview?.totalFunds || 0}
          icon={FiDollarSign}
          color="bg-green-500"
        />
      </div>

      {/* Growth & Breakdown Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Breakdown */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-secondary mb-4">
            User Breakdown
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Applicants</span>
              <div className="flex items-center gap-3">
                <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{
                      width: `${
                        overview?.totalUsers
                          ? (userBreakdown?.applicants / overview.totalUsers) * 100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <span className="font-semibold text-secondary w-12 text-right">
                  {userBreakdown?.applicants || 0}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Organizations</span>
              <div className="flex items-center gap-3">
                <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-purple-500 rounded-full"
                    style={{
                      width: `${
                        overview?.totalUsers
                          ? (userBreakdown?.organizations / overview.totalUsers) * 100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <span className="font-semibold text-secondary w-12 text-right">
                  {userBreakdown?.organizations || 0}
                </span>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <h4 className="text-sm font-medium text-gray-500 mb-3">
            Auth Providers
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(userBreakdown?.authProviders || {}).map(
              ([provider, count]) => (
                <div
                  key={provider}
                  className="bg-gray-50 rounded-lg p-3 text-center"
                >
                  <p className="text-2xl font-bold text-secondary">{count}</p>
                  <p className="text-sm text-gray-500 capitalize">{provider}</p>
                </div>
              )
            )}
          </div>
        </div>

        {/* Project Breakdown */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-secondary mb-4">
            Project States
          </h3>
          <div className="space-y-3">
            {Object.entries(projectBreakdown?.byState || {}).map(
              ([state, count]) => (
                <div key={state} className="flex items-center justify-between">
                  <span className="text-gray-600">{state}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{
                          width: `${
                            overview?.totalProjects
                              ? (count / overview.totalProjects) * 100
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                    <span className="font-semibold text-secondary w-12 text-right">
                      {count}
                    </span>
                  </div>
                </div>
              )
            )}
          </div>

          <hr className="my-4" />

          <h4 className="text-sm font-medium text-gray-500 mb-3">
            Recent Activity
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-600">
                {growth?.newUsersLast7Days || 0}
              </p>
              <p className="text-sm text-gray-500">New users (7d)</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">
                {growth?.newProjectsLast30Days || 0}
              </p>
              <p className="text-sm text-gray-500">New projects (30d)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-secondary">Recent Users</h3>
            <Link
              to="/admin/users"
              className="text-primary hover:text-light-primary flex items-center gap-1 text-sm"
            >
              View all <FiArrowRight size={14} />
            </Link>
          </div>
          <div className="space-y-1">
            {recent?.users?.map((user) => (
              <RecentItem key={user._id} item={user} type="user" />
            ))}
            {(!recent?.users || recent.users.length === 0) && (
              <p className="text-gray-500 text-center py-4">No recent users</p>
            )}
          </div>
        </div>

        {/* Recent Projects */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-secondary">
              Recent Projects
            </h3>
            <Link
              to="/admin/projects"
              className="text-primary hover:text-light-primary flex items-center gap-1 text-sm"
            >
              View all <FiArrowRight size={14} />
            </Link>
          </div>
          <div className="space-y-1">
            {recent?.projects?.map((project) => (
              <RecentItem key={project._id} item={project} type="project" />
            ))}
            {(!recent?.projects || recent.projects.length === 0) && (
              <p className="text-gray-500 text-center py-4">No recent projects</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
